'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/shared/lib/api';
import type { Project } from '@/shared/lib/types';
import { useProjectsPage } from '@/features/projects/hooks/useProjectsPage';
import { useSessionStore } from '@/store/sessionStore';
import { translate } from '@/shared/lib/locales';
import { useModal } from '@/shared/providers/ModalProvider';

export function useHomePage() {
  const { confirm, alert } = useModal();
  const router = useRouter();
  const sessionId = useSessionStore((s) => s.sessionId);
  const {
    projects,
    isLoading,
    loadProjects,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    total,
    sortBy,
    sortOrder,
    onSortChange,
  } = useProjectsPage(sessionId ?? '');
  const [shareProject, setShareProject] = useState<Project | null>(null);

  const handleCreateProject = async () => {
    try {
      const projectId = await api.createProject(translate('homePage', 'defaultProjectName'));
      router.push(`/projects/${projectId}?editName=1`);
    } catch (error) {
      await alert(translate('hookMessages', 'createProjectWithReasonError', { message: (error as Error).message }));
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    const confirmed = await confirm({
      message: translate('hookMessages', 'deleteProjectConfirm'),
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await api.deleteProject(projectId);
      loadProjects();
    } catch (error) {
      await alert((error as Error).message || translate('hookMessages', 'deleteProjectError'));
    }
  };

  const handleManageCollaborators = (projectId: string) => {
    const selectedProject = projects.find((project) => project.project_id === projectId) ?? null;
    setShareProject(selectedProject);
  };

  const handleCloseShareProject = () => {
    setShareProject(null);
  };

  const handleShareSaved = () => {
    setShareProject(null);
    loadProjects();
  };

  return {
    projects,
    isLoading,
    shareProject,
    handleCreateProject,
    handleDeleteProject,
    handleManageCollaborators,
    handleCloseShareProject,
    handleShareSaved,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
    total,
    sortBy,
    sortOrder,
    onSortChange,
  };
}
